// PreviewCard — inline generative-UI preview card for the Aidea Assistant
// chat UI (Task B2b).
//
// A reusable card the assistant renders inline to PROPOSE generated content
// before it is committed to the scene. Four variants are driven by `type`:
//   - "scene" : an "infographic scene" — 3 timed visuals + a reveal timeline.
//   - "image" : a single 16:9 image + a caption/prompt line.
//   - "video" : a 16:9 thumbnail with a play overlay + a duration chip.
//   - "quiz"  : a question line + radio-style option rows.
//
// This is a STATIC visual demo. Every action button is a no-op (console.log) —
// there is no real generation or backend wiring here. Styling follows the same
// scoping contract as ChatPanel: `tw-`-prefixed utilities under
// `.aidea-chat-root`, brand tokens via CSS vars (`--aidea-primary`, etc.),
// Lucide icons via `react-icons/lu`. No emoji.

import React from "react";
import {
  CheckIcon,
  WandIcon,
  CloseIcon,
  PlayIcon,
  ChevronDownIcon,
  ImageIcon,
  VideoIcon,
  InfographicIcon,
  QuizIcon,
} from "./icons";

// Shared violet focus ring (kept in sync with ChatPanel's FOCUS_RING).
const FOCUS_RING =
  "focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[var(--aidea-primary)] focus-visible:tw-ring-offset-1 focus-visible:tw-ring-offset-white";

// Per-type chip metadata (label + icon) for the small top type-chip.
const TYPE_META = {
  scene: { label: "Scene", Icon: InfographicIcon },
  image: { label: "Image", Icon: ImageIcon },
  video: { label: "Video", Icon: VideoIcon },
  quiz: { label: "Quiz", Icon: QuizIcon },
};

// Static demo data for the "scene" variant: 3 visuals timed to narration.
const SCENE_VISUALS = [
  { seed: "aidea1", time: "0:00–0:04" },
  { seed: "aidea2", time: "0:04–0:09" },
  { seed: "aidea3", time: "0:09–0:14" },
];

// Static demo data for the "quiz" variant.
const QUIZ_OPTIONS = [
  { text: "It reduces cognitive load by pacing visuals to the narration", correct: true },
  { text: "It makes the slide load faster", correct: false },
  { text: "It removes the need for narration entirely", correct: false },
  { text: "It only works for video exports", correct: false },
];

function noop(action) {
  // Demo only — replace with real handlers when wiring to the runtime.
  // eslint-disable-next-line no-console
  console.log(`[PreviewCard] ${action} (demo no-op)`);
}

// ---------------------------------------------------------------------------
// Top type-chip — small violet-tint pill, e.g. "Scene".
// ---------------------------------------------------------------------------
function TypeChip({ type }) {
  const meta = TYPE_META[type] ?? TYPE_META.scene;
  const { label, Icon } = meta;
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <Icon size={12} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Variant bodies
// ---------------------------------------------------------------------------

// Scene: a row of 3 timed thumbnails + a thin reveal-timeline bar under them.
function SceneBody() {
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-grid tw-grid-cols-3 tw-gap-1.5">
        {SCENE_VISUALS.map((v, i) => (
          <figure
            key={v.seed}
            className="tw-relative tw-m-0 tw-overflow-hidden tw-rounded-lg tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-muted-bg)]"
          >
            <img
              src={`https://picsum.photos/seed/${v.seed}/240/135`}
              alt={`Visual ${i + 1} of the infographic scene`}
              width={240}
              height={135}
              loading="lazy"
              className="tw-block tw-aspect-video tw-w-full tw-object-cover"
            />
            <figcaption className="tw-absolute tw-bottom-1 tw-left-1 tw-rounded-md tw-bg-black/65 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-medium tw-tabular-nums tw-text-white">
              {v.time}
            </figcaption>
          </figure>
        ))}
      </div>

      {/* Reveal timeline: thin track with three evenly-spaced segments to
          suggest the 3 visuals being revealed across the narration. */}
      <div
        className="tw-flex tw-items-center tw-gap-0.5"
        aria-hidden="true"
        title="Reveal timeline"
      >
        <span className="tw-h-1.5 tw-flex-1 tw-rounded-full tw-bg-[var(--aidea-primary)]" />
        <span className="tw-h-1.5 tw-flex-1 tw-rounded-full tw-bg-[var(--aidea-primary)] tw-opacity-70" />
        <span className="tw-h-1.5 tw-flex-1 tw-rounded-full tw-bg-[var(--aidea-primary)] tw-opacity-45" />
      </div>
    </div>
  );
}

// Image: single 16:9 image + a caption / prompt line.
function ImageBody({ caption }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-muted-bg)]">
        <img
          src="https://picsum.photos/seed/aidea-image/640/360"
          alt={caption || "Generated image preview"}
          width={640}
          height={360}
          loading="lazy"
          className="tw-block tw-aspect-video tw-w-full tw-object-cover"
        />
      </div>
      <p className="tw-m-0 tw-text-[12px] tw-italic tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
        {caption ||
          "“A flat-style illustration of a team collaborating around a shared dashboard.”"}
      </p>
    </div>
  );
}

// Video: 16:9 thumbnail + centered play overlay + a duration chip.
function VideoBody({ duration = "0:08" }) {
  return (
    <div className="tw-relative tw-overflow-hidden tw-rounded-lg tw-border tw-border-[var(--aidea-border)] tw-bg-black">
      <img
        src="https://picsum.photos/seed/aidea-video/640/360"
        alt="Generated video preview"
        width={640}
        height={360}
        loading="lazy"
        className="tw-block tw-aspect-video tw-w-full tw-object-cover tw-opacity-90"
      />
      <span className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
        <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/90 tw-text-[var(--aidea-primary)] tw-shadow-md">
          {/* Nudge the play triangle to feel optically centered. */}
          <PlayIcon size={20} className="tw-ml-0.5" />
        </span>
      </span>
      <span className="tw-absolute tw-bottom-1.5 tw-right-1.5 tw-rounded-md tw-bg-black/70 tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-medium tw-tabular-nums tw-text-white">
        {duration}
      </span>
    </div>
  );
}

// Quiz: a question line + radio-style option rows.
function QuizBody() {
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <p className="tw-m-0 tw-text-[13px] tw-font-medium tw-leading-snug tw-text-[var(--aidea-fg)]">
        Why does pacing visuals to the narration help learners?
      </p>
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
        {QUIZ_OPTIONS.map((opt, i) => (
          <li
            key={i}
            className={`tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-px-2.5 tw-py-2 tw-text-[12.5px] tw-leading-snug ${
              opt.correct
                ? "tw-border-[var(--aidea-primary)] tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-fg)]"
                : "tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-text-[var(--aidea-fg)]"
            }`}
          >
            <span
              aria-hidden="true"
              className={`tw-flex tw-h-4 tw-w-4 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-border ${
                opt.correct
                  ? "tw-border-[var(--aidea-primary)]"
                  : "tw-border-[var(--aidea-muted-fg)]"
              }`}
            >
              {opt.correct && (
                <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-[var(--aidea-primary)]" />
              )}
            </span>
            <span className="tw-min-w-0">{opt.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Footer action buttons
// ---------------------------------------------------------------------------
function GhostButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`tw-inline-flex tw-h-9 tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-2.5 tw-text-[13px] tw-font-medium tw-text-[var(--aidea-muted-fg)] tw-transition-colors tw-duration-200 tw-cursor-pointer hover:tw-bg-[var(--aidea-muted-bg)] hover:tw-text-[var(--aidea-fg)] ${FOCUS_RING}`}
    >
      {children}
    </button>
  );
}

function OutlineButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`tw-inline-flex tw-h-9 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-px-2.5 tw-text-[13px] tw-font-medium tw-text-[var(--aidea-fg)] tw-transition-colors tw-duration-200 tw-cursor-pointer hover:tw-border-[var(--aidea-primary)] hover:tw-text-[var(--aidea-primary)] ${FOCUS_RING}`}
    >
      {children}
    </button>
  );
}

// Primary "Accept" with a small split "Add to: …" affordance. Both halves are
// static; the split is a labeled visual only (no real menu).
function AcceptSplit({ target = "Current", onAccept, onPickTarget }) {
  return (
    <span className="tw-inline-flex tw-items-stretch tw-overflow-hidden tw-rounded-lg tw-shadow-sm">
      <button
        type="button"
        aria-label="Accept and add to scene"
        title="Accept and add to scene"
        onClick={onAccept}
        className={`tw-inline-flex tw-h-9 tw-items-center tw-gap-1.5 tw-bg-[var(--aidea-primary)] tw-pl-3 tw-pr-2.5 tw-text-[13px] tw-font-semibold tw-text-[var(--aidea-on-primary)] tw-transition-colors tw-duration-200 tw-cursor-pointer hover:tw-bg-[var(--aidea-primary-hover)] ${FOCUS_RING}`}
      >
        <CheckIcon size={15} />
        Accept
      </button>
      <button
        type="button"
        aria-label={`Add to: ${target} (choose target scene)`}
        title={`Add to: ${target}`}
        onClick={onPickTarget}
        className={`tw-inline-flex tw-h-9 tw-items-center tw-gap-1 tw-border-l tw-border-white/25 tw-bg-[var(--aidea-primary)] tw-pl-2 tw-pr-2.5 tw-text-[12px] tw-font-medium tw-text-[var(--aidea-on-primary)] tw-transition-colors tw-duration-200 tw-cursor-pointer hover:tw-bg-[var(--aidea-primary-hover)] ${FOCUS_RING}`}
      >
        <span className="tw-opacity-85">{target}</span>
        <ChevronDownIcon size={13} className="tw-opacity-85" />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// PreviewCard — card chrome (type chip, title, body, footer action bar).
// ---------------------------------------------------------------------------
export default function PreviewCard({
  type = "scene",
  title,
  caption,
  duration,
  addTarget = "Current",
}) {
  // Default titles per variant; `title` prop overrides.
  const defaultTitle =
    {
      scene: "Infographic scene · 3 visuals",
      image: "Generated image",
      video: "Generated video",
      quiz: "Knowledge check · 1 question",
    }[type] ?? "Preview";

  // "Animate" is offered for scene/image only (per spec).
  const showAnimate = type === "scene" || type === "image";

  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[440px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {/* Header: type chip + title */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <TypeChip type={type} />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-font-semibold tw-text-[var(--aidea-fg)]">
          {title || defaultTitle}
        </span>
      </div>

      {/* Body */}
      <div className="tw-px-3 tw-py-2.5">
        {type === "scene" && <SceneBody />}
        {type === "image" && <ImageBody caption={caption} />}
        {type === "video" && <VideoBody duration={duration} />}
        {type === "quiz" && <QuizBody />}
      </div>

      {/* Footer action bar */}
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-border-t tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-muted-bg)] tw-px-3 tw-py-2">
        <AcceptSplit
          target={addTarget}
          onAccept={() => noop("Accept → add to scene")}
          onPickTarget={() => noop("Pick add target (Current / New scene)")}
        />

        {showAnimate && (
          <OutlineButton label="Animate this preview" onClick={() => noop("Animate")}>
            <WandIcon size={15} />
            Animate
          </OutlineButton>
        )}

        <div className="tw-ml-auto">
          <GhostButton label="Discard this preview" onClick={() => noop("Discard")}>
            <CloseIcon size={15} />
            Discard
          </GhostButton>
        </div>
      </div>
    </div>
  );
}
